import type { GexStrikeData, GexMetric } from '../types/gex';

/**
 * Call / put walls for the gamma profile.
 *
 * Lives outside GexProfileWidget so it can be tested directly, and so that
 * widget keeps Fast Refresh — a non-component export from a component file
 * silently disables it.
 */

export interface GexWalls {
  callWall: GexStrikeData | null;
  callWallMinor: GexStrikeData | null;
  putWall: GexStrikeData | null;
  putWallMinor: GexStrikeData | null;
}

/**
 * Rank the walls on net exposure (call + put of the selected metric), by SIGN:
 *
 *   Call Wall Major  = largest positive exposure
 *   Call Wall Minor  = second largest positive
 *   Put Wall Major   = largest negative (most negative)
 *   Put Wall Minor   = second most negative
 *
 * Spot is irrelevant: a wall is where exposure sits, not which side of the
 * price it happens to fall on.
 *
 * ⚠️ **Pass the WHOLE chain, never the strikes inside the visible window.**
 * Ranking only what was on screen made the walls jump on every zoom/pan, and
 * made the default view disagree with Fuzion Levels, which ranks every strike
 * (portal `GammaExposureService._calculate_gex_stats`). A wall outside the
 * window is simply not drawn until the user scrolls to it.
 *
 * Ranking the raw fields by absolute size was an earlier bug — `put_gex` is
 * largest wherever put exposure is heaviest, even at a strike whose NET is
 * positive, so a blue bar could be labelled Put Wall Major. Ranked on net in
 * both display modes for the same reason: switching between Net and
 * Calls/Puts changes the presentation, so the levels must not move under it.
 */
export function rankWalls(gex: GexStrikeData[] | undefined, metric: GexMetric): GexWalls {
  const callField = `call_${metric}` as keyof GexStrikeData;
  const putField = `put_${metric}` as keyof GexStrikeData;
  const ranked = (gex ?? []).map((d) => ({
    d,
    v: (Number(d[callField]) || 0) + (Number(d[putField]) || 0),
  }));

  const positives = ranked.filter((x) => x.v > 0).sort((a, b) => b.v - a.v).map((x) => x.d);
  const negatives = ranked.filter((x) => x.v < 0).sort((a, b) => a.v - b.v).map((x) => x.d);

  return {
    callWall: positives[0] ?? null,
    callWallMinor: positives[1] ?? null,
    putWall: negatives[0] ?? null,
    putWallMinor: negatives[1] ?? null,
  };
}
